import { context, metrics, propagation, SpanKind, SpanStatusCode, trace } from '@opentelemetry/api';
import { logs } from '@opentelemetry/api-logs';
import { getWebAutoInstrumentations } from '@opentelemetry/auto-instrumentations-web';
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http';
import { OTLPMetricExporter } from '@opentelemetry/exporter-metrics-otlp-http';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { BatchLogRecordProcessor, LoggerProvider } from '@opentelemetry/sdk-logs';
import { MeterProvider, PeriodicExportingMetricReader } from '@opentelemetry/sdk-metrics';
import { BatchSpanProcessor, ParentBasedSampler, TraceIdRatioBasedSampler } from '@opentelemetry/sdk-trace-base';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { normalizeRoutePath } from './normalizeRoute';

type TelemetryAttributeValue = string | number | boolean | string[] | number[] | boolean[];

type TelemetryAttributes = Record<string, TelemetryAttributeValue>;

type UrlRule = string | RegExp;

export type TelemetryConfig = {
  projectName?: string;
  serviceName?: string;
  serviceNamespace?: string;
  environment?: string;
  region?: string;
  tracePropagationUrls?: UrlRule[];
  skipRequestUrls?: UrlRule[];
  metricExportIntervalMillis?: number;
  apiRouteNormalizer?: (url: URL) => string;
  enableWebVitals?: boolean;
  enablePageLoadMetrics?: boolean;
  traceSampleRate?: number;
  firstPartyHosts?: string[];
};

const DEFAULT_PROJECT_NAME = 'gmsb-frontend-components';
const DEFAULT_SERVICE_NAME = 'gmsb-frontend-app';
const DEFAULT_SERVICE_NAMESPACE = 'frontend';
const DEFAULT_METRIC_EXPORT_INTERVAL = 1_800_000;

let initialized = false;
let fetchWrapped = false;
let errorsInstrumented = false;
let consoleErrorsInstrumented = false;
let emittingConsoleError = false;
let enableInfoLogs = false;
let enableErrorLogs = false;
let enableWarnLogs = false;
let enableWebVitalsMetrics = false;
let enablePageLoadMetricsFlag = false;

let projectName: string = DEFAULT_PROJECT_NAME;
let serviceName: string = DEFAULT_SERVICE_NAME;
let serviceNamespace: string = DEFAULT_SERVICE_NAMESPACE;
let deploymentEnvironment: string = process.env.NODE_ENV ?? 'unknown';
let region: string = 'unknown';
const appVersion = process.env.NEXT_PUBLIC_APP_VERSION ?? 'unknown';

let configuredTracePropagationUrls: UrlRule[] = [];
let configuredSkipRequestUrls: UrlRule[] = [];
let configuredFirstPartyHosts: string[] = [];

let configuredApiRouteNormalizer: ((url: URL) => string) | undefined;

let lastTrackedPageKey: string | undefined;
let lastTrackedPageTimestamp = 0;

let telemetryLogger: ReturnType<typeof logs.getLogger> | undefined;

let pageViewCounter: ReturnType<ReturnType<typeof metrics.getMeter>['createCounter']> | undefined;

let apiRequestCounter: ReturnType<ReturnType<typeof metrics.getMeter>['createCounter']> | undefined;

let apiFailureCounter: ReturnType<ReturnType<typeof metrics.getMeter>['createCounter']> | undefined;

let apiDurationHistogram: ReturnType<ReturnType<typeof metrics.getMeter>['createHistogram']> | undefined;

let pageLoadDurationHistogram: ReturnType<ReturnType<typeof metrics.getMeter>['createHistogram']> | undefined;

function getSessionAttributes(): TelemetryAttributes {
  if (typeof document === 'undefined') {
    return {};
  }

  // A sandboxed iframe without allow-same-origin throws SecurityError on cookie access.
  let cookie = '';

  try {
    cookie = document.cookie;
  } catch {
    return {};
  }

  const match = cookie.match(/(?:^|;\s*)frontastic-session=([^;]*)/);

  if (!match?.[1]) {
    return {};
  }

  try {
    return {
      sessionId: decodeURIComponent(match[1]),
    };
  } catch {
    return {
      sessionId: match[1],
    };
  }
}

function createRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  const array = new Uint32Array(1);
  crypto.getRandomValues(array);
  return `${Date.now()}-${array[0].toString(16)}`;
}

function getDeviceType(): string {
  if (typeof navigator === 'undefined') {
    return 'unknown';
  }

  const userAgent = navigator.userAgent;

  if (/ipad|tablet/i.test(userAgent)) {
    return 'tablet';
  }

  if (/mobile|iphone|ipod|android/i.test(userAgent)) {
    return 'mobile';
  }

  return 'desktop';
}

function getTraceAttributes(): TelemetryAttributes {
  const activeSpan = trace.getSpan(context.active());

  if (!activeSpan) {
    return {};
  }

  const spanContext = activeSpan.spanContext();

  return {
    traceId: spanContext.traceId,
    spanId: spanContext.spanId,
  };
}

function getCommonAttributes(): TelemetryAttributes {
  return {
    ...getTraceAttributes(),
    project: projectName,
    environment: deploymentEnvironment,
    serviceName,
    serviceNamespace,
    ...getSessionAttributes(),
    currentPage: getCurrentPageUrl(),
    currentPath: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
    currentRoute: getCurrentPageRoute(),
    referrer: typeof document !== 'undefined' ? document.referrer || 'direct' : 'unknown',
    browser: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    language: typeof navigator !== 'undefined' ? navigator.language : 'unknown',
    platform: typeof navigator !== 'undefined' ? navigator.platform : 'unknown',
    deviceType: getDeviceType(),
    viewport: typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : 'unknown',
    appVersion,
    region,
  };
}

function getMetricAttributes(): TelemetryAttributes {
  return {
    project: projectName,
    environment: deploymentEnvironment,
    region,
    service: serviceName,
    namespace: serviceNamespace,
  };
}

// location.origin is the string "null" in a sandboxed iframe, which is not a usable URL base.
function parseUrl(value: string): URL {
  const base = typeof window !== 'undefined' ? window.location.href : '';

  return new URL(value, base || 'http://localhost');
}
const SENSITIVE_QUERY_PARAMETERS = new Set([
  'access_token',
  'authorization',
  'code',
  'email',
  'id_token',
  'jwt',
  'password',
  'refresh_token',
  'reset_token',
  'session',
  'token',
]);

function getFullMetricUrl(value: string): string {
  try {
    const parsedUrl = parseUrl(value);

    for (const parameterName of Array.from(parsedUrl.searchParams.keys())) {
      if (SENSITIVE_QUERY_PARAMETERS.has(parameterName.toLowerCase())) {
        parsedUrl.searchParams.set(parameterName, 'REDACTED');
      }
    }

    parsedUrl.hash = '';

    return parsedUrl.toString();
  } catch {
    return value.split('#')[0];
  }
}

function getCurrentPageUrl(): string {
  if (typeof window === 'undefined') {
    return 'unknown';
  }

  return window.location.href;
}

function getCurrentPageRoute(): string {
  if (typeof window === 'undefined') {
    return 'unknown';
  }

  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function getErrorStack(error: unknown): string {
  if (error instanceof Error && error.stack) {
    return error.stack;
  }

  return 'unknown';
}

function normalizeApiRoute(value: string): string {
  try {
    const parsedUrl = parseUrl(value);

    if (configuredApiRouteNormalizer) {
      return configuredApiRouteNormalizer(parsedUrl);
    }

    return normalizeRoutePath(parsedUrl.pathname);
  } catch {
    return value.split('?')[0].split('#')[0] || 'unknown';
  }
}

function getRequestHost(value: string): string {
  try {
    return parseUrl(value).hostname || 'unknown';
  } catch {
    return 'unknown';
  }
}

// Suffix match is anchored on a dot so `fujifilm.com.example.net` cannot pass as first party.
function getRequestParty(value: string): string {
  try {
    const hostname = parseUrl(value).hostname;

    if (
      (typeof window !== 'undefined' && hostname === window.location.hostname) ||
      configuredFirstPartyHosts.some((host) => hostname === host || hostname.endsWith(`.${host}`))
    ) {
      return 'first_party';
    }

    return 'third_party';
  } catch {
    return 'unknown';
  }
}

function getRequestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') {
    return input;
  }

  if (input instanceof URL) {
    return input.toString();
  }

  return input.url;
}

function getRequestMethod(input: RequestInfo | URL, init?: RequestInit): string {
  if (init?.method) {
    return init.method.toUpperCase();
  }

  if (typeof Request !== 'undefined' && input instanceof Request) {
    return input.method.toUpperCase();
  }

  return 'GET';
}

function matchesUrlRule(url: string, rule: UrlRule): boolean {
  if (typeof rule === 'string') {
    return url.startsWith(rule);
  }

  rule.lastIndex = 0;

  return rule.test(url);
}

function isTelemetryRequest(value: string): boolean {
  try {
    const pathname = parseUrl(value).pathname;

    return pathname.endsWith('/v1/logs') || pathname.endsWith('/v1/traces') || pathname.endsWith('/v1/metrics');
  } catch {
    return value.includes('/v1/logs') || value.includes('/v1/traces') || value.includes('/v1/metrics');
  }
}

function isAuthenticationRequest(value: string): boolean {
  try {
    const pathname = parseUrl(value).pathname.toLowerCase();

    return (
      pathname === '/login' ||
      pathname.endsWith('/login') ||
      pathname.includes('/api/auth/') ||
      pathname.includes('/auth/') ||
      pathname.includes('/oauth/') ||
      pathname.includes('/oidc/') ||
      pathname.includes('/sso/') ||
      pathname.includes('/token') ||
      pathname.includes('/logout') ||
      pathname.includes('/session') ||
      pathname.includes('/callback')
    );
  } catch {
    return false;
  }
}

function isConfiguredSkipRequest(value: string): boolean {
  try {
    const absoluteUrl = parseUrl(value).toString();

    return configuredSkipRequestUrls.some((rule) => matchesUrlRule(absoluteUrl, rule));
  } catch {
    return false;
  }
}

const STATIC_ASSET_EXTENSION = /\.(?:css|js|mjs|cjs|map|woff2?|ttf|otf|eot|svg|png|jpe?g|gif|webp|avif|ico|bmp)$/i;

function isStaticAssetRequest(value: string): boolean {
  try {
    const pathname = parseUrl(value).pathname;

    return (
      pathname.includes('/_next/static/') ||
      pathname.includes('/_next/image') ||
      pathname.includes('hot-update') ||
      STATIC_ASSET_EXTENSION.test(pathname)
    );
  } catch {
    return false;
  }
}

function shouldBypassInstrumentation(value: string): boolean {
  return (
    isTelemetryRequest(value) ||
    isAuthenticationRequest(value) ||
    isStaticAssetRequest(value) ||
    isConfiguredSkipRequest(value)
  );
}

function shouldPropagateTraceHeaders(value: string): boolean {
  try {
    const parsedUrl = parseUrl(value);
    const absoluteUrl = parsedUrl.toString();

    if (typeof window !== 'undefined' && parsedUrl.origin === window.location.origin) {
      return true;
    }

    return configuredTracePropagationUrls.some((rule) => matchesUrlRule(absoluteUrl, rule));
  } catch {
    return false;
  }
}

function getCorrelationAttributes(response: Response): TelemetryAttributes {
  const correlationId =
    response.headers.get('x-correlation-id') ??
    response.headers.get('x-request-id') ??
    response.headers.get('request-id') ??
    response.headers.get('x-amzn-requestid');

  if (!correlationId) {
    return {};
  }

  return {
    correlationId,
  };
}

function getParentSpanAttributes(): TelemetryAttributes {
  const activeSpan = trace.getSpan(context.active());

  if (!activeSpan) {
    return {};
  }

  return {
    parentSpanId: activeSpan.spanContext().spanId,
  };
}

function buildFetchArguments(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  requestId: string,
  injectTraceHeaders: boolean,
): {
  input: RequestInfo | URL;
  init?: RequestInit;
} {
  if (!injectTraceHeaders) {
    return {
      input,
      init,
    };
  }

  const headers = new Headers(
    init?.headers ?? (typeof Request !== 'undefined' && input instanceof Request ? input.headers : undefined),
  );

  if (!headers.has('x-request-id')) {
    headers.set('x-request-id', requestId);
  }

  propagation.inject(context.active(), headers, {
    set(carrier, key, value) {
      carrier.set(key, String(value));
    },
  });

  return {
    input,
    init: {
      ...init,
      headers,
    },
  };
}

function emitErrorLog(body: string, attributes?: TelemetryAttributes): void {
  if (!enableErrorLogs) {
    return;
  }

  telemetryLogger?.emit({
    severityText: 'ERROR',
    severityNumber: 17,
    body,
    attributes: {
      ...getCommonAttributes(),
      ...attributes,
    },
  });
}

function serializeConsoleValue(value: unknown): string {
  if (value instanceof Error) {
    return value.stack ? `${value.name}: ${value.message}\n${value.stack}` : `${value.name}: ${value.message}`;
  }

  if (typeof value === 'string') {
    return value;
  }

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function instrumentConsoleErrors(): void {
  if (consoleErrorsInstrumented || typeof console === 'undefined') {
    return;
  }

  consoleErrorsInstrumented = true;

  console.error = (...args: unknown[]): void => {
    if (!enableErrorLogs || emittingConsoleError || !initialized) {
      return;
    }

    const firstArgument = args[0];

    if (typeof firstArgument === 'string' && firstArgument.startsWith('[Telemetry]')) {
      return;
    }

    emittingConsoleError = true;

    try {
      const error = args.find((argument) => argument instanceof Error);

      const message = args.map(serializeConsoleValue).join(' ');

      emitErrorLog('Browser console error', {
        source: 'browser.console.error',
        message: message || 'Unknown console error',
        errorName: error instanceof Error ? error.name : 'ConsoleError',
        errorMessage: error instanceof Error ? error.message : message,
        errorStack: getErrorStack(error),
        failureReason: error instanceof Error ? error.message : message,
        pageUrl: getCurrentPageUrl(),
        pageRoute: getCurrentPageRoute(),
        spanStatus: 'ERROR',
        status: 'ERROR',
      });
    } finally {
      emittingConsoleError = false;
    }
  };

  console.warn = (...args: unknown[]): void => {
    const message = args.map(serializeConsoleValue).join(' ');

    if (!initialized || !enableWarnLogs) {
      return;
    }

    telemetryLogger?.emit({
      severityText: 'WARN',
      severityNumber: 13,
      body: 'Browser console warning',
      attributes: {
        ...getCommonAttributes(),
        source: 'browser.console.warn',
        message,
      },
    });
  };
  console.info = (...args: unknown[]): void => {
    if (!initialized || !enableInfoLogs) {
      return;
    }

    const message = args.map(serializeConsoleValue).join(' ');

    telemetryLogger?.emit({
      severityText: 'INFO',
      severityNumber: 9,
      body: 'Browser console info',
      attributes: {
        ...getCommonAttributes(),
        source: 'browser.console.info',
        message,
      },
    });
  };
}

export function logError(body: string, attributes?: TelemetryAttributes): void {
  const completeAttributes = {
    ...getCommonAttributes(),
    ...attributes,
  };

  emitErrorLog(body, completeAttributes);
}

export function logInfo(body: string, attributes?: TelemetryAttributes): void {
  if (!enableInfoLogs) {
    return;
  }
  telemetryLogger?.emit({
    severityText: 'INFO',
    severityNumber: 9,
    body,
    attributes: {
      ...getCommonAttributes(),
      ...attributes,
    },
  });
}

export function logWarn(body: string, attributes?: TelemetryAttributes): void {
  if (!enableWarnLogs) {
    return;
  }
  telemetryLogger?.emit({
    severityText: 'WARN',
    severityNumber: 13,
    body,
    attributes: {
      ...getCommonAttributes(),
      ...attributes,
    },
  });
}

function sanitizeAuditAttributes(attributes: TelemetryAttributes = {}): TelemetryAttributes {
  const sanitized = { ...attributes };

  delete sanitized.password;
  delete sanitized.token;
  delete sanitized.accessToken;
  delete sanitized.refreshToken;
  delete sanitized.authorization;
  delete sanitized.email;
  delete sanitized.jwt;

  return sanitized;
}

export function logAuditEvent(action: string, attributes?: TelemetryAttributes): void {
  if (!enableInfoLogs) {
    return;
  }

  telemetryLogger?.emit({
    severityText: 'INFO',
    severityNumber: 9,
    body: `AUDIT: ${action}`,
    attributes: {
      ...getCommonAttributes(),
      eventType: 'AUDIT',
      action,
      timestamp: new Date().toISOString(),
      ...sanitizeAuditAttributes(attributes),
    },
  });
}

export function logUserAuditEvent(action: string, userId: string, attributes?: TelemetryAttributes): void {
  logAuditEvent(action, {
    userId,
    ...attributes,
  });
}

export function initializeTelemetry(
  otelTraces: string,
  otelMetrics: string,
  otelLogs: string,
  otelLogInfo: boolean,
  otelLogError: boolean,
  otelLogWarn: boolean,
  config: TelemetryConfig = {},
): boolean {
  if (initialized) {
    return true;
  }

  if (typeof window === 'undefined') {
    return false;
  }

  if (!otelTraces || !otelMetrics || !otelLogs) {
    return false;
  }

  projectName = config.projectName ?? DEFAULT_PROJECT_NAME;

  serviceName = config.serviceName ?? DEFAULT_SERVICE_NAME;

  serviceNamespace = config.serviceNamespace ?? DEFAULT_SERVICE_NAMESPACE;

  deploymentEnvironment = config.environment ?? process.env.NODE_ENV ?? 'unknown';

  region = config.region ?? process.env.NEXT_PUBLIC_APP_REGION ?? 'unknown';

  configuredTracePropagationUrls = config.tracePropagationUrls ?? [];

  configuredSkipRequestUrls = config.skipRequestUrls ?? [];

  configuredFirstPartyHosts = (config.firstPartyHosts ?? []).map((host) => host.trim().toLowerCase()).filter(Boolean);

  configuredApiRouteNormalizer = config.apiRouteNormalizer;
  enableInfoLogs = otelLogInfo;
  enableErrorLogs = otelLogError;
  enableWarnLogs = otelLogWarn;
  enableWebVitalsMetrics = config.enableWebVitals ?? false;
  enablePageLoadMetricsFlag = config.enablePageLoadMetrics ?? false;

  const traceSampleRate = Math.min(1, Math.max(0, config.traceSampleRate ?? 1.0));

  const resource = resourceFromAttributes({
    'service.name': serviceName,
    'service.namespace': serviceNamespace,
    'deployment.environment.name': deploymentEnvironment,
    'deployment.environment': deploymentEnvironment,
    'deployment.region': region,
    'app.project.name': projectName,
    'browser.user_agent': navigator.userAgent,
    'browser.language': navigator.language,
  });

  const traceExporter = new OTLPTraceExporter({
    url: otelTraces,
  });

  const tracerProvider = new WebTracerProvider({
    resource,
    spanProcessors: [new BatchSpanProcessor(traceExporter)],
    sampler: new ParentBasedSampler({
      root: new TraceIdRatioBasedSampler(traceSampleRate),
    }),
  });

  tracerProvider.register();

  const metricExporter = new OTLPMetricExporter({
    url: otelMetrics,
  });

  const metricReader = new PeriodicExportingMetricReader({
    exporter: metricExporter,
    exportIntervalMillis: config.metricExportIntervalMillis ?? DEFAULT_METRIC_EXPORT_INTERVAL,
  });

  const meterProvider = new MeterProvider({
    resource,
    readers: [metricReader],
  });

  metrics.setGlobalMeterProvider(meterProvider);

  const meter = metrics.getMeter('frontend-meter');

  pageViewCounter = meter.createCounter('frontend_page_views_total', {
    description: 'Total frontend page views',
  });

  apiRequestCounter = meter.createCounter('frontend_api_requests_total', {
    description: 'Total completed frontend API requests',
  });

  apiFailureCounter = meter.createCounter('frontend_api_failures_total', {
    description: 'Total failed frontend API requests',
  });

  apiDurationHistogram = meter.createHistogram('frontend_api_request_duration', {
    description: 'Frontend API request duration in milliseconds',
    unit: 'ms',
    advice: {
      explicitBucketBoundaries: [5, 10, 25, 50, 100, 250, 500, 1000, 2500, 5000, 10000],
    },
  });

  if (enablePageLoadMetricsFlag) {
    pageLoadDurationHistogram = meter.createHistogram('frontend_page_load_duration', {
      description: 'Frontend page load duration in milliseconds',
      unit: 'ms',
      advice: {
        explicitBucketBoundaries: [100, 500, 1000, 2000, 3000, 5000, 10000],
      },
    });
  }

  const loggerProvider = new LoggerProvider({
    resource,
    processors: [
      new BatchLogRecordProcessor({
        exporter: new OTLPLogExporter({
          url: otelLogs,
        }),
        scheduledDelayMillis: 10_000,
        maxExportBatchSize: 20,
      }),
    ],
  });

  logs.setGlobalLoggerProvider(loggerProvider);

  telemetryLogger = logs.getLogger('frontend-logger');

  registerInstrumentations({
    instrumentations: getWebAutoInstrumentations({
      '@opentelemetry/instrumentation-fetch': {
        enabled: false,
      },
      '@opentelemetry/instrumentation-xml-http-request': {
        enabled: false,
      },
      '@opentelemetry/instrumentation-document-load': {
        enabled: true,
      },
      '@opentelemetry/instrumentation-user-interaction': {
        enabled: true,
      },
    }),
  });

  instrumentFetchLogging();
  instrumentFrontendErrors();

  initialized = true;

  instrumentConsoleErrors();

  if (enablePageLoadMetricsFlag) {
    recordPageLoadMetrics();
  }

  registerFlushOnUnload(tracerProvider, meterProvider, loggerProvider);

  logAuditEvent('TELEMETRY_INITIALIZED');

  return true;
}

export function trackPageView(path: string, routeName = path): void {
  if (!initialized) {
    return;
  }

  const normalizedPath = path.split('?')[0].split('#')[0] || '/';

  const pageKey = `${routeName}:${normalizedPath}`;

  const now = Date.now();

  if (lastTrackedPageKey === pageKey && now - lastTrackedPageTimestamp < 1_000) {
    return;
  }

  lastTrackedPageKey = pageKey;
  lastTrackedPageTimestamp = now;

  pageViewCounter?.add(1, {
    ...getMetricAttributes(),
    path: normalizedPath,
    route: routeName,
  });
}

export function isWebVitalsEnabled(): boolean {
  return initialized && enableWebVitalsMetrics;
}

function recordPageLoadMetrics(): void {
  if (typeof performance === 'undefined' || typeof performance.getEntriesByType !== 'function') {
    return;
  }

  const tryRecord = () => {
    const entries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    if (entries.length === 0) {
      return;
    }
    const nav = entries[0];
    if (nav.loadEventEnd > 0) {
      const durationMs = Math.round(nav.loadEventEnd - nav.startTime);
      pageLoadDurationHistogram?.record(durationMs, {
        ...getMetricAttributes(),
        path: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
      });
    }
  };

  if (document.readyState === 'complete') {
    tryRecord();
  } else {
    window.addEventListener('load', () => tryRecord(), { once: true });
  }
}

function registerFlushOnUnload(
  tracerProvider: WebTracerProvider,
  meterProvider: MeterProvider,
  loggerProvider: LoggerProvider,
): void {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      tracerProvider.forceFlush().catch(() => {});
      meterProvider.forceFlush().catch(() => {});
      loggerProvider.forceFlush().catch(() => {});
    }
  });
}

// TODO: this is not even being used
export async function withPageSpan<T>(routeName: string, path: string, operation: () => Promise<T>): Promise<T> {
  if (!initialized) {
    return operation();
  }

  const tracer = trace.getTracer('frontend-navigation');

  const normalizedPath = path.split('?')[0].split('#')[0] || '/';

  return tracer.startActiveSpan(
    `Route: ${routeName}`,
    {
      kind: SpanKind.INTERNAL,
      attributes: {
        'app.project.name': projectName,
        'deployment.environment.name': deploymentEnvironment,
        'service.name': serviceName,
        'app.route.name': routeName,
        'url.path': normalizedPath,
        'page.route': routeName,
        'page.path': normalizedPath,
      },
    },
    async (span) => {
      const startTime = performance.now();

      try {
        const result = await operation();

        const durationMs = Math.round(performance.now() - startTime);

        span.setAttribute('page.duration_ms', durationMs);

        span.setStatus({
          code: SpanStatusCode.OK,
        });

        return result;
      } catch (error) {
        const normalizedError = error instanceof Error ? error : new Error(String(error));

        const durationMs = Math.round(performance.now() - startTime);

        span.recordException(normalizedError);

        span.setAttributes({
          'page.duration_ms': durationMs,
          'error.type': normalizedError.name,
          'error.message': normalizedError.message,
        });

        span.setStatus({
          code: SpanStatusCode.ERROR,
          message: normalizedError.message,
        });

        logError('Route operation failed', {
          routeName,
          path: normalizedPath,
          durationMs,
          status: 'ERROR',
          failureReason: normalizedError.message,
          errorName: normalizedError.name,
        });

        throw error;
      } finally {
        span.end();
      }
    },
  );
}

function instrumentFetchLogging(): void {
  if (fetchWrapped) {
    return;
  }

  fetchWrapped = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const rawUrl = getRequestUrl(input);

    if (shouldBypassInstrumentation(rawUrl)) {
      return originalFetch(input, init);
    }

    const fullMetricUrl = getFullMetricUrl(rawUrl);

    const route = normalizeApiRoute(rawUrl);

    const requestHost = getRequestHost(rawUrl);

    const requestParty = getRequestParty(rawUrl);

    const method = getRequestMethod(input, init);

    const requestId = createRequestId();

    const parentSpanAttributes = getParentSpanAttributes();

    const parentSpanId =
      typeof parentSpanAttributes.parentSpanId === 'string' ? parentSpanAttributes.parentSpanId : undefined;

    const tracer = trace.getTracer('frontend-http-client');

    return tracer.startActiveSpan(
      `${method} ${route}`,
      {
        kind: SpanKind.CLIENT,
        attributes: {
          'app.project.name': projectName,
          'deployment.environment.name': deploymentEnvironment,
          'service.name': serviceName,
          'http.request.method': method,
          'url.full': fullMetricUrl,
          'url.path': route,
          'server.address': requestHost,
          'app.request.party': requestParty,
          'app.api.route': route,
          'app.request.id': requestId,
          ...(parentSpanId
            ? {
                'app.parent.span_id': parentSpanId,
              }
            : {}),
        },
      },
      async (span) => {
        const startTime = performance.now();

        const fetchArguments = buildFetchArguments(input, init, requestId, shouldPropagateTraceHeaders(rawUrl));

        try {
          const response = await originalFetch(fetchArguments.input, fetchArguments.init);

          const durationMs = Math.round(performance.now() - startTime);

          const outcome = response.ok ? 'success' : 'failure';

          const correlationAttributes = getCorrelationAttributes(response);

          const correlationId =
            typeof correlationAttributes.correlationId === 'string' ? correlationAttributes.correlationId : undefined;

          const metricAttributes: TelemetryAttributes = {
            ...getMetricAttributes(),
            route,
            host: requestHost,
            party: requestParty,
            method,
            http_status_code: String(response.status),
            outcome,
          };

          apiRequestCounter?.add(1, metricAttributes);

          apiDurationHistogram?.record(durationMs, metricAttributes);

          span.setAttributes({
            'http.response.status_code': response.status,
            'http.response.status_text': response.statusText || 'unknown',
            ...(correlationId
              ? {
                  'app.correlation.id': correlationId,
                }
              : {}),
            'app.request.duration_ms': durationMs,
            'app.frontend.response_time_ms': durationMs,
            'app.request.outcome': outcome,
            'app.response.received': true,
            'app.span.status': response.ok ? 'OK' : 'ERROR',
          });

          if (response.ok) {
            span.setStatus({
              code: SpanStatusCode.OK,
            });
          } else {
            apiFailureCounter?.add(1, metricAttributes);

            const failureReason = response.statusText || `HTTP ${response.status}`;

            span.setAttributes({
              'error.type': `HTTP_${response.status}`,
              'error.message': failureReason,
            });

            span.setStatus({
              code: SpanStatusCode.ERROR,
              message: failureReason,
            });

            logError('API request failed', {
              url: fullMetricUrl,
              route,
              method,
              requestId,
              ...correlationAttributes,
              ...parentSpanAttributes,
              httpStatusCode: response.status,
              statusText: response.statusText || 'Unknown HTTP error',
              responseReceived: true,
              spanStatus: 'ERROR',
              status: 'ERROR',
              outcome,
              durationMs,
              frontendResponseTimeMs: durationMs,
              failureReason,
            });
          }

          return response;
        } catch (error) {
          const durationMs = Math.round(performance.now() - startTime);

          const normalizedError = error instanceof Error ? error : new Error(String(error));

          const metricAttributes: TelemetryAttributes = {
            ...getMetricAttributes(),
            route,
            host: requestHost,
            party: requestParty,
            method,
            http_status_code: 'NO_RESPONSE',
            outcome: 'network_error',
            error_type: normalizedError.name,
          };

          apiRequestCounter?.add(1, metricAttributes);

          apiFailureCounter?.add(1, metricAttributes);

          apiDurationHistogram?.record(durationMs, metricAttributes);

          span.recordException(normalizedError);

          span.setAttributes({
            'app.request.duration_ms': durationMs,
            'app.frontend.response_time_ms': durationMs,
            'app.request.outcome': 'network_error',
            'app.response.received': false,
            'app.span.status': 'ERROR',
            'error.type': normalizedError.name,
            'error.message': normalizedError.message,
          });

          span.setStatus({
            code: SpanStatusCode.ERROR,
            message: normalizedError.message,
          });

          // Raw browser fetch network-failure strings (dropped connection, tab backgrounded,
          // aborted navigation) — downgrade to WARN instead of ERROR to cut support noise.
          const isTransientNetworkError = [
            'Load failed',
            'Failed to fetch',
            'NetworkError when attempting to fetch resource.',
            'The Internet connection appears to be offline.',
            'signal is aborted without reason',
            'The operation was aborted.',
          ].includes(normalizedError.message);

          (isTransientNetworkError ? logWarn : logError)('API request exception', {
            url: fullMetricUrl,
            route,
            fullUrl: fullMetricUrl,
            method,
            requestId,
            ...parentSpanAttributes,
            httpStatusCode: 'NO_RESPONSE',
            responseReceived: false,
            spanStatus: isTransientNetworkError ? 'WARN' : 'ERROR',
            status: isTransientNetworkError ? 'WARN' : 'ERROR',
            outcome: 'network_error',
            durationMs,
            frontendResponseTimeMs: durationMs,
            errorName: normalizedError.name,
            errorMessage: normalizedError.message,
            failureReason: normalizedError.message,
          });

          throw error;
        } finally {
          span.end();
        }
      },
    );
  };
}

function instrumentFrontendErrors(): void {
  if (errorsInstrumented) {
    return;
  }

  errorsInstrumented = true;

  window.addEventListener('error', (event) => {
    if (
      event.target instanceof HTMLImageElement ||
      event.target instanceof HTMLScriptElement ||
      event.target instanceof HTMLLinkElement
    ) {
      return;
    }
    const tracer = trace.getTracer('frontend-errors');

    tracer.startActiveSpan(
      'Unhandled frontend error',
      {
        kind: SpanKind.INTERNAL,
        attributes: {
          'app.project.name': projectName,
          'deployment.environment.name': deploymentEnvironment,
          'service.name': serviceName,
          'url.full': getCurrentPageUrl(),
          'url.path': window.location.pathname,
          'app.page.route': getCurrentPageRoute(),
          'app.span.status': 'ERROR',
        },
      },
      (span) => {
        const normalizedError =
          event.error instanceof Error ? event.error : new Error(event.message || 'Unknown frontend error');

        try {
          span.recordException(normalizedError);

          span.setAttributes({
            'error.type': normalizedError.name,
            'error.message': normalizedError.message,
            'error.stack': getErrorStack(normalizedError),
            'code.filepath': event.filename || 'unknown',
            'code.lineno': event.lineno,
            'code.column': event.colno,
            'app.span.status': 'ERROR',
          });

          span.setStatus({
            code: SpanStatusCode.ERROR,
            message: normalizedError.message,
          });

          logError('Unhandled frontend error', {
            source: 'window.error',
            message: normalizedError.message,
            filename: event.filename || 'unknown',
            lineNumber: event.lineno,
            columnNumber: event.colno,
            errorName: normalizedError.name,
            errorStack: getErrorStack(normalizedError),
            pageUrl: getCurrentPageUrl(),
            pageRoute: getCurrentPageRoute(),
            spanStatus: 'ERROR',
            status: 'ERROR',
            failureReason: normalizedError.message,
          });
        } finally {
          span.end();
        }
      },
    );
  });
  window.addEventListener(
    'error',
    (event) => {
      const target = event.target;

      if (
        target instanceof HTMLImageElement ||
        target instanceof HTMLScriptElement ||
        target instanceof HTMLLinkElement
      ) {
        logError('Resource load failure', {
          source: 'resource.error',
          resourceType: target.tagName,
          resourceUrl:
            (target as HTMLImageElement).src ||
            (target as HTMLScriptElement).src ||
            (target as HTMLLinkElement).href ||
            'unknown',
          pageUrl: getCurrentPageUrl(),
        });
      }
    },
    true,
  );

  window.addEventListener('unhandledrejection', (event) => {
    const tracer = trace.getTracer('frontend-errors');

    tracer.startActiveSpan(
      'Unhandled promise rejection',
      {
        kind: SpanKind.INTERNAL,
        attributes: {
          'app.project.name': projectName,
          'deployment.environment.name': deploymentEnvironment,
          'service.name': serviceName,
          'url.full': getCurrentPageUrl(),
          'url.path': window.location.pathname,
          'app.page.route': getCurrentPageRoute(),
          'app.span.status': 'ERROR',
        },
      },
      (span) => {
        const normalizedError = event.reason instanceof Error ? event.reason : new Error(String(event.reason));

        try {
          span.recordException(normalizedError);

          span.setAttributes({
            'error.type': normalizedError.name,
            'error.message': normalizedError.message,
            'error.stack': getErrorStack(normalizedError),
            'app.span.status': 'ERROR',
          });

          span.setStatus({
            code: SpanStatusCode.ERROR,
            message: normalizedError.message,
          });

          logError('Unhandled promise rejection', {
            source: 'window.unhandledrejection',
            reason: normalizedError.message,
            errorName: normalizedError.name,
            errorStack: getErrorStack(normalizedError),
            pageUrl: getCurrentPageUrl(),
            pageRoute: getCurrentPageRoute(),
            spanStatus: 'ERROR',
            status: 'ERROR',
            failureReason: normalizedError.message,
          });
        } finally {
          span.end();
        }
      },
    );
  });
}
